import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Autocomplete,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Event as EventIcon,
  Favorite as CampaignIcon,
  LinkOff as UnlinkIcon,
  SellOutlined as EsgotadoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import eventsService, { Event } from '../../services/events.service';
import api from '../../services/api';

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  DRAFT: 'default',
  draft: 'default',
  PUBLISHED: 'success',
  published: 'success',
  CANCELLED: 'error',
  cancelled: 'error',
  COMPLETED: 'primary',
  completed: 'primary',
  POSTPONED: 'warning',
};

const statusLabels: Record<string, string> = {
  DRAFT: 'Rascunho',
  draft: 'Rascunho',
  PUBLISHED: 'Publicado',
  published: 'Publicado',
  CANCELLED: 'Cancelado',
  cancelled: 'Cancelado',
  COMPLETED: 'Concluído',
  completed: 'Concluído',
  POSTPONED: 'Adiado',
};

interface CampaignOption { ID: string; TITULO: string; VALOR_ARRECADADO?: number; META_VALOR?: number }

const EventsPage: React.FC = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<Event[]>([]);
  // Interruptor global da contagem de bilhetes (pg_config EVENTOS_OCULTAR_CONTAGEM)
  const [contagemOculta, setContagemOculta] = useState<boolean | null>(null);
  const [confirmarContagem, setConfirmarContagem] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Link campanha
  const [linkDialog, setLinkDialog] = useState(false);
  const [campaignOptions, setCampaignOptions] = useState<CampaignOption[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignOption | null>(null);
  const [campaignInput, setCampaignInput] = useState('');
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);
  const campaignDebounce = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchCampaigns = async (q: string) => {
    if (!q || q.trim().length < 2) { setCampaignOptions([]); setCampaignLoading(false); return; }
    setCampaignLoading(true);
    try {
      const res = await api.get(`/admin/donations/campaigns?search=${encodeURIComponent(q.trim())}&limit=10`);
      setCampaignOptions(res.data?.data || []);
    } catch { setCampaignOptions([]); } finally { setCampaignLoading(false); }
  };

  const handleCampaignInputChange = (_: React.SyntheticEvent, val: string) => {
    setCampaignInput(val);
    if (campaignDebounce.current) clearTimeout(campaignDebounce.current);
    if (!val || val.trim().length < 2) { setCampaignOptions([]); return; }
    setCampaignLoading(true);
    campaignDebounce.current = setTimeout(() => searchCampaigns(val), 350);
  };

  const handleLinkCampaign = async () => {
    if (!selectedEvent) return;
    setLinkSaving(true);
    try {
      await api.put(`/admin/events/${selectedEvent.id}`, { campanha_id: selectedCampaign?.ID || null });
      setLinkDialog(false);
      setSelectedCampaign(null);
      setCampaignInput('');
      fetchEvents();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao ligar campanha');
    } finally { setLinkSaving(false); }
  };

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await eventsService.getEvents({ search: searchTerm || undefined, limit: 50 });
      setEvents(result.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchEvents();
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchTerm === '') { fetchEvents(); return; }
    const t = setTimeout(fetchEvents, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const handleToggleEsgotado = async () => {
    if (!selectedEvent) return;
    try {
      await api.put(`/admin/events/${selectedEvent.id}`, { flag_esgotado: !selectedEvent.flag_esgotado });
      fetchEvents();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao atualizar estado');
    } finally { handleMenuClose(); }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, eventData: Event) => {
    setAnchorEl(event.currentTarget);
    setSelectedEvent(eventData);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEvent(null);
  };

  const handleCreateEvent = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('pt-PT', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  useEffect(() => {
    api.get('/admin/config')
      .then((res) => setContagemOculta(res.data.data?.EVENTOS_OCULTAR_CONTAGEM?.value !== 'false'))
      .catch(() => setContagemOculta(null));
  }, []);

  const mudarContagem = async (ocultar: boolean) => {
    setConfirmarContagem(null);
    try {
      await api.put('/admin/config', { EVENTOS_OCULTAR_CONTAGEM: ocultar ? 'true' : 'false' });
      setContagemOculta(ocultar);
    } catch (e: any) {
      setError(e?.response?.data?.errors?.join(' · ') || e?.response?.data?.error || 'Não foi possível mudar o interruptor');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Eventos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerir os seus eventos
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateEvent}
        >
          Novo Evento
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {contagemOculta !== null && (
        <Alert severity={contagemOculta ? 'success' : 'warning'} sx={{ mb: 2 }}
          action={
            <FormControlLabel
              control={<Switch checked={contagemOculta} onChange={(e) => setConfirmarContagem(e.target.checked)} />}
              label={contagemOculta ? 'Escondida' : 'Cada evento decide'}
            />
          }>
          {contagemOculta
            ? 'Contagem de bilhetes: escondida do público em todos os eventos.'
            : 'Contagem de bilhetes: cada evento decide, na opção do portal do promotor.'}
        </Alert>
      )}

      <Dialog open={confirmarContagem !== null} onClose={() => setConfirmarContagem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmarContagem ? 'Esconder a contagem em todos os eventos?' : 'Deixar cada evento decidir?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmarContagem
              ? 'Nenhum evento mostra ao público quantos bilhetes foram vendidos ou restam, mesmo que o promotor o queira.'
              : 'Os eventos em que o promotor não escondeu a contagem voltam a mostrar os números ao público, no site e na app.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarContagem(null)}>Cancelar</Button>
          <Button variant="contained" color={confirmarContagem ? 'primary' : 'warning'}
            onClick={() => mudarContagem(!!confirmarContagem)}>
            {confirmarContagem ? 'Esconder' : 'Deixar decidir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Pesquisar eventos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Evento</TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Local</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Bilhetes vendidos</TableCell>
              <TableCell>Campanha</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={36} />
                </TableCell>
              </TableRow>
            ) : events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <EventIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Nenhum evento encontrado
                  </Typography>
                  <Typography variant="body2" color="text.disabled" sx={{ mb: 2 }}>
                    {searchTerm ? 'Tente outro termo de pesquisa' : 'Crie o primeiro evento para começar'}
                  </Typography>
                  {!searchTerm && (
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleCreateEvent}>
                      Criar Evento
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => (
                <TableRow key={event.id} hover>
                  <TableCell>
                    <Typography fontWeight="medium">{event.titulo}</Typography>
                  </TableCell>
                  <TableCell>{event.categoria || '—'}</TableCell>
                  <TableCell>
                    {[event.local_nome, event.local_cidade].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell>{formatDate(event.data_inicio)}</TableCell>
                  <TableCell>
                    {event.total_bilhetes_vendidos ?? 0}
                    {event.local_capacidade ? ` / ${event.local_capacidade}` : ''}
                  </TableCell>
                  <TableCell>
                    {(event as any).campanha_id ? (
                      <Tooltip title={(event as any).campanha_titulo || 'Campanha ligada'}>
                        <Chip size="small" icon={<CampaignIcon />} label={(event as any).campanha_titulo || 'Campanha'} color="secondary" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip
                        label={statusLabels[event.status || ''] || event.status || '—'}
                        color={statusColors[event.status || ''] || 'default'}
                        size="small"
                      />
                      {event.flag_esgotado && (
                        <Chip label="Esgotado" color="warning" size="small" icon={<EsgotadoIcon />} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, event)}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleMenuClose}>
          <ViewIcon sx={{ mr: 1 }} fontSize="small" /> Ver Detalhes
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" /> Editar
        </MenuItem>
        <MenuItem onClick={handleToggleEsgotado}>
          <EsgotadoIcon sx={{ mr: 1 }} fontSize="small" color="warning" />
          {selectedEvent?.flag_esgotado ? 'Remover Esgotado' : 'Marcar como Esgotado'}
        </MenuItem>
        <MenuItem onClick={() => { handleMenuClose(); setLinkDialog(true); setSelectedCampaign(null); setCampaignInput(''); setCampaignOptions([]); }}>
          <CampaignIcon sx={{ mr: 1 }} fontSize="small" color="secondary" /> Ligar / Desligar Campanha
        </MenuItem>
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" /> Eliminar
        </MenuItem>
      </Menu>

      {/* Link Campaign Dialog */}
      <Dialog open={linkDialog} onClose={() => setLinkDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CampaignIcon color="secondary" /> Ligar Campanha ao Evento
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Evento: <strong>{selectedEvent?.titulo}</strong>
          </Typography>
          <Autocomplete
            options={campaignOptions}
            value={selectedCampaign}
            inputValue={campaignInput}
            loading={campaignLoading}
            filterOptions={(x) => x}
            getOptionLabel={(o) => o.TITULO}
            isOptionEqualToValue={(o, v) => o.ID === v.ID}
            noOptionsText={campaignInput.trim().length < 2 ? 'Escreva 2+ caracteres para pesquisar' : 'Nenhuma campanha encontrada'}
            onInputChange={handleCampaignInputChange}
            onChange={(_, val) => { setSelectedCampaign(val); setCampaignInput(val ? val.TITULO : ''); }}
            renderInput={(params) => (
              <TextField {...params} label="Campanha de crowdfunding" placeholder="Pesquisar campanha..."
                InputProps={{ ...params.InputProps, endAdornment: <>{campaignLoading && <CircularProgress size={16} />}{params.InputProps.endAdornment}</> }} />
            )}
          />
          {(selectedEvent as any)?.campanha_id && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <UnlinkIcon fontSize="small" color="warning" />
              <Typography variant="caption" color="warning.main">
                Actualmente ligado a: <strong>{(selectedEvent as any)?.campanha_titulo || (selectedEvent as any)?.campanha_id}</strong>. Selecciona uma nova ou deixa em branco para desligar.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {(selectedEvent as any)?.campanha_id && (
            <Button color="warning" onClick={async () => {
              setLinkSaving(true);
              try { await api.put(`/admin/events/${selectedEvent!.id}`, { campanha_id: null }); setLinkDialog(false); fetchEvents(); } catch { } finally { setLinkSaving(false); }
            }} disabled={linkSaving}>Desligar</Button>
          )}
          <Button onClick={() => setLinkDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="secondary" onClick={handleLinkCampaign} disabled={linkSaving || !selectedCampaign}>
            {linkSaving ? <CircularProgress size={16} /> : 'Ligar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Novo Evento</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth label="Nome do Evento" required />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <InputLabel>Categoria</InputLabel>
                <Select label="Categoria">
                  <MenuItem value="CONCERT">Música / Concerto</MenuItem>
                  <MenuItem value="FESTIVAL">Festival</MenuItem>
                  <MenuItem value="CONFERENCE">Conferência</MenuItem>
                  <MenuItem value="WORKSHOP">Workshop</MenuItem>
                  <MenuItem value="SPORTS">Desporto</MenuItem>
                  <MenuItem value="THEATER">Teatro</MenuItem>
                  <MenuItem value="EXHIBITION">Exposição</MenuItem>
                  <MenuItem value="PARTY">Festa</MenuItem>
                  <MenuItem value="OTHER">Outro</MenuItem>
                </Select>
              </FormControl>
              <TextField sx={{ flex: '1 1 45%', minWidth: 200 }} label="Local" required />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField sx={{ flex: '1 1 45%', minWidth: 200 }} label="Cidade" required />
              <TextField
                sx={{ flex: '1 1 45%', minWidth: 200 }}
                label="Data de Início"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                sx={{ flex: '1 1 45%', minWidth: 200 }}
                label="Data de Fim"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
              />
              <TextField sx={{ flex: '1 1 45%', minWidth: 200 }} label="Capacidade Total" type="number" />
            </Box>
            <TextField fullWidth label="Descrição" multiline rows={4} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleCloseDialog}>
            Criar Evento
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventsPage;
